import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Progress } from '../../components/ui/progress';
import { 
  Power, PowerOff, RotateCcw, Activity, AlertCircle, CheckCircle, 
  ExternalLink, Server, Zap, Database, Search, Globe, CreditCard,
  Loader2, RefreshCw, Coins, TrendingUp, Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API Services Configuration
const API_SERVICES = [
  {
    id: 'emergent_llm',
    name: 'Emergent LLM Key',
    description: 'Multi-AI Zugang (OpenAI, Gemini, Claude) für Diagnosen und Spec-Collector',
    icon: Zap,
    color: '#7CD4FD',
    providerUrl: 'https://emergent.sh/profile',
    settingsKey: 'emergent_llm',
    hasCredits: true,
    creditInfo: {
      checkUrl: 'https://emergent.sh/profile',
      topUpUrl: 'https://emergent.sh/profile'
    }
  },
  {
    id: 'openai',
    name: 'OpenAI API',
    description: 'GPT-4o für Diagnosen (DEV-Modus) und Validierung',
    icon: Zap,
    color: '#10B981',
    providerUrl: 'https://platform.openai.com/usage',
    settingsKey: 'openai',
    hasCredits: true,
    creditInfo: {
      checkUrl: 'https://platform.openai.com/usage',
      topUpUrl: 'https://platform.openai.com/account/billing'
    }
  },
  {
    id: 'tavily',
    name: 'Tavily Search',
    description: 'Web-Recherche für aktuelle Fahrzeug-Informationen',
    icon: Search,
    color: '#8B5CF6',
    providerUrl: 'https://tavily.com',
    settingsKey: 'tavily',
    hasCredits: true,
    creditInfo: {
      checkUrl: 'https://app.tavily.com/home',
      topUpUrl: 'https://app.tavily.com/home'
    }
  },
  {
    id: 'exchangerate',
    name: 'ExchangeRate API',
    description: 'Live-Wechselkurse für Geo-Pricing (EUR/CHF/USD)',
    icon: Globe,
    color: '#F59E0B',
    providerUrl: 'https://www.exchangerate-api.com',
    settingsKey: 'currency'
  },
  {
    id: 'stripe',
    name: 'Stripe Payments',
    description: 'Zahlungsabwicklung für Abonnements',
    icon: CreditCard,
    color: '#6366F1',
    providerUrl: 'https://dashboard.stripe.com',
    settingsKey: 'stripe'
  }
];

export const AdminServiceDashboard = () => {
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [collectorMode, setCollectorMode] = useState(null);
  const [creditUsage, setCreditUsage] = useState({
    emergent_llm: { used: 0, total: 0, available: 243 },
    openai: { used: 0, total: 0 },
    tavily: { used: 0, total: 1000 }
  });

  const fetchServiceStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch multiple status endpoints in parallel
      const [settingsRes, modeRes, creditRes] = await Promise.all([
        axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/spec-collector/mode`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { dev_mode: true } })),
        axios.get(`${API}/admin/api-credits`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      
      // Build service status from settings
      const settings = settingsRes.data;
      const serviceStatus = {};
      
      // Check each service
      serviceStatus.emergent_llm = {
        configured: true, // Always configured via env
        status: 'active'
      };
      
      serviceStatus.openai = {
        configured: !!settings.openai?.openai_api_key,
        status: settings.openai?.openai_api_key ? 'active' : 'not_configured'
      };
      
      serviceStatus.tavily = {
        configured: !!settings.tavily?.tavily_api_key,
        status: settings.tavily?.tavily_api_key ? 'active' : 'not_configured'
      };
      
      serviceStatus.stripe = {
        configured: !!settings.stripe?.stripe_api_key,
        status: settings.stripe?.stripe_api_key ? 'active' : 'not_configured'
      };
      
      // Check currency API separately
      try {
        const currencyRes = await axios.get(`${API}/admin/settings/currency`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        serviceStatus.exchangerate = {
          configured: currencyRes.data.api_key_configured,
          status: 'active' // Always works with free tier
        };
      } catch {
        serviceStatus.exchangerate = { configured: false, status: 'active' };
      }
      
      // Update credit usage from backend
      if (creditRes.data) {
        setCreditUsage(creditRes.data);
      }
      
      setServices(serviceStatus);
      setCollectorMode(modeRes.data);
      
      // Check if AI is globally enabled (from settings or default)
      const aiSettings = await axios.get(`${API}/admin/settings/ai-status`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { enabled: true } }));
      setAiEnabled(aiSettings.data?.enabled ?? true);
      
    } catch (error) {
      console.error('Failed to fetch service status:', error);
      toast.error('Fehler beim Laden der Service-Status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServiceStatus();
  }, [fetchServiceStatus]);

  const handleToggleAI = async () => {
    setActionLoading('ai_toggle');
    try {
      const token = localStorage.getItem('token');
      const newState = !aiEnabled;
      
      await axios.post(`${API}/admin/settings/ai-status`, 
        { enabled: newState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAiEnabled(newState);
      toast.success(newState ? 'KI-Services aktiviert' : 'KI-Services deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Ändern des KI-Status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleDevMode = async () => {
    setActionLoading('dev_mode');
    try {
      const token = localStorage.getItem('token');
      const newDevMode = !collectorMode?.dev_mode;
      
      await axios.post(`${API}/admin/spec-collector/mode`, 
        { dev_mode: newDevMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCollectorMode({ ...collectorMode, dev_mode: newDevMode });
      toast.success(newDevMode ? 'DEV-Modus aktiviert (Credit-Sparend)' : 'PROD-Modus aktiviert (Volle Power)', {
        description: 'Server-Neustart für permanente Änderung erforderlich'
      });
    } catch (error) {
      toast.error('Fehler beim Ändern des Modus');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestartBackend = async () => {
    if (!window.confirm('Backend-Server wirklich neustarten? Laufende Anfragen werden abgebrochen.')) {
      return;
    }
    
    setActionLoading('restart');
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/admin/system/restart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Backend wird neugestartet...', {
        description: 'Bitte warten Sie 10-15 Sekunden'
      });
      
      // Wait and refresh
      setTimeout(() => {
        fetchServiceStatus();
      }, 15000);
      
    } catch (error) {
      // Even if we get an error, the restart might have worked
      toast.info('Restart initiiert - Seite wird in 15 Sekunden aktualisiert');
      setTimeout(() => {
        fetchServiceStatus();
      }, 15000);
    } finally {
      setTimeout(() => setActionLoading(null), 15000);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Aktiv</Badge>;
      case 'not_configured':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Nicht konfiguriert</Badge>;
      case 'error':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Fehler</Badge>;
      case 'disabled':
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Deaktiviert</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Unbekannt</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#7CD4FD' }} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
            Service & API Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
            Übersicht und Steuerung aller integrierten Dienste
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchServiceStatus}
          style={{ borderColor: '#1F2937', color: '#94A3B8' }}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Service Control Panel */}
      <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
            <Server className="w-5 h-5" />
            Service-Steuerung
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* AI Global Toggle */}
            <div 
              className="p-4 rounded-lg border-2 transition-all"
              style={{ 
                backgroundColor: aiEnabled ? '#22C55E15' : '#EF444415',
                borderColor: aiEnabled ? '#22C55E' : '#EF4444'
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {aiEnabled ? (
                    <Power className="w-6 h-6" style={{ color: '#22C55E' }} />
                  ) : (
                    <PowerOff className="w-6 h-6" style={{ color: '#EF4444' }} />
                  )}
                  <span className="font-bold text-lg" style={{ color: '#E6EDF3' }}>
                    KI-Services
                  </span>
                </div>
                <Switch
                  checked={aiEnabled}
                  onCheckedChange={handleToggleAI}
                  disabled={actionLoading === 'ai_toggle'}
                />
              </div>
              <div 
                className="text-center py-2 rounded-md font-bold text-sm"
                style={{ 
                  backgroundColor: aiEnabled ? '#22C55E30' : '#EF444430',
                  color: aiEnabled ? '#22C55E' : '#EF4444'
                }}
              >
                {aiEnabled ? '✓ AKTIV' : '✗ DEAKTIVIERT'}
              </div>
              <p className="text-xs mt-2 text-center" style={{ color: '#94A3B8' }}>
                {aiEnabled ? 'Alle KI-Funktionen verfügbar' : 'Alle KI-Funktionen pausiert'}
              </p>
            </div>

            {/* DEV/PROD Mode Toggle */}
            <div 
              className="p-4 rounded-lg border-2 transition-all"
              style={{ 
                backgroundColor: collectorMode?.dev_mode ? '#22C55E15' : '#F59E0B15',
                borderColor: collectorMode?.dev_mode ? '#22C55E' : '#F59E0B'
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-6 h-6" style={{ color: collectorMode?.dev_mode ? '#22C55E' : '#F59E0B' }} />
                  <span className="font-bold text-lg" style={{ color: '#E6EDF3' }}>
                    Modus
                  </span>
                </div>
                <Switch
                  checked={!collectorMode?.dev_mode}
                  onCheckedChange={handleToggleDevMode}
                  disabled={actionLoading === 'dev_mode'}
                />
              </div>
              <div 
                className="text-center py-2 rounded-md font-bold text-sm"
                style={{ 
                  backgroundColor: collectorMode?.dev_mode ? '#22C55E30' : '#F59E0B30',
                  color: collectorMode?.dev_mode ? '#22C55E' : '#F59E0B'
                }}
              >
                {collectorMode?.dev_mode ? '💰 DEV (Spar-Modus)' : '⚡ PROD (Volle Power)'}
              </div>
              <p className="text-xs mt-2 text-center" style={{ color: '#94A3B8' }}>
                {collectorMode?.dev_mode ? 'Nur OpenAI - weniger Credits' : 'Gemini + GPT-4o + Claude'}
              </p>
            </div>

            {/* Restart Button */}
            <div 
              className="p-4 rounded-lg border-2"
              style={{ 
                backgroundColor: '#1F2937',
                borderColor: '#374151'
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-6 h-6" style={{ color: '#7CD4FD' }} />
                  <span className="font-bold text-lg" style={{ color: '#E6EDF3' }}>
                    Backend
                  </span>
                </div>
                <Button
                  size="sm"
                  onClick={handleRestartBackend}
                  disabled={actionLoading === 'restart'}
                  style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                >
                  {actionLoading === 'restart' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Neustart'
                  )}
                </Button>
              </div>
              <div 
                className="text-center py-2 rounded-md font-bold text-sm"
                style={{ backgroundColor: '#374151', color: '#94A3B8' }}
              >
                🔄 Server-Neustart
              </div>
              <p className="text-xs mt-2 text-center" style={{ color: '#94A3B8' }}>
                Für permanente Modus-Änderungen
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Usage Card */}
      <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
            <Coins className="w-5 h-5" style={{ color: '#F59E0B' }} />
            Credit-Nutzung
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Emergent LLM Credits */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" style={{ color: '#7CD4FD' }} />
                  <span className="font-semibold" style={{ color: '#E6EDF3' }}>Emergent LLM</span>
                </div>
                <a
                  href="https://emergent.sh/profile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs flex items-center gap-1 hover:underline"
                  style={{ color: '#7CD4FD' }}
                >
                  Aufladen <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold" style={{ color: creditUsage.emergent_llm?.available > 50 ? '#22C55E' : creditUsage.emergent_llm?.available > 10 ? '#F59E0B' : '#EF4444' }}>
                  {creditUsage.emergent_llm?.available || 0}
                </span>
                <span style={{ color: '#94A3B8' }}>Credits verfügbar</span>
              </div>
              <Progress 
                value={Math.min(100, (creditUsage.emergent_llm?.available || 0) / 5)} 
                className="h-2"
                style={{ 
                  backgroundColor: '#374151',
                  '--progress-color': creditUsage.emergent_llm?.available > 50 ? '#22C55E' : creditUsage.emergent_llm?.available > 10 ? '#F59E0B' : '#EF4444'
                }}
              />
              <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                <Info className="w-3 h-3 inline mr-1" />
                Profil → Universal Key → Add Balance
              </p>
            </div>

            {/* OpenAI Credits */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5" style={{ color: '#10B981' }} />
                  <span className="font-semibold" style={{ color: '#E6EDF3' }}>OpenAI</span>
                </div>
                <a
                  href="https://platform.openai.com/usage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs flex items-center gap-1 hover:underline"
                  style={{ color: '#7CD4FD' }}
                >
                  Prüfen <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold" style={{ color: '#10B981' }}>
                  {creditUsage.openai?.remaining != null ? `$${creditUsage.openai.remaining}` : '—'}
                </span>
                <span style={{ color: '#94A3B8' }}>verbleibend</span>
              </div>
              <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                <TrendingUp className="w-3 h-3 inline mr-1" />
                Nutzung auf platform.openai.com prüfen
              </p>
            </div>

            {/* Tavily Credits */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5" style={{ color: '#8B5CF6' }} />
                  <span className="font-semibold" style={{ color: '#E6EDF3' }}>Tavily Search</span>
                </div>
                <a
                  href="https://app.tavily.com/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs flex items-center gap-1 hover:underline"
                  style={{ color: '#7CD4FD' }}
                >
                  Prüfen <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-3xl font-bold" style={{ color: '#8B5CF6' }}>
                  {creditUsage.tavily?.remaining !== undefined ? creditUsage.tavily.remaining : '1000'}
                </span>
                <span style={{ color: '#94A3B8' }}>/ {creditUsage.tavily?.total || 1000} Anfragen</span>
              </div>
              <Progress 
                value={(creditUsage.tavily?.remaining || 1000) / (creditUsage.tavily?.total || 1000) * 100} 
                className="h-2"
                style={{ backgroundColor: '#374151' }}
              />
              <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                <Info className="w-3 h-3 inline mr-1" />
                Free Tier: 1000 Anfragen/Monat
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Services Overview */}
      <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
            <Database className="w-5 h-5" />
            API-Dienste Übersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {API_SERVICES.map((service) => {
              const status = services[service.id] || {};
              const Icon = service.icon;
              
              return (
                <div 
                  key={service.id}
                  className="p-4 rounded-lg flex items-center justify-between"
                  style={{ backgroundColor: '#1F2937' }}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${service.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: service.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: '#E6EDF3' }}>
                          {service.name}
                        </span>
                        {getStatusBadge(status.status || 'not_configured')}
                      </div>
                      <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {status.configured ? (
                      <CheckCircle className="w-5 h-5" style={{ color: '#22C55E' }} />
                    ) : (
                      <AlertCircle className="w-5 h-5" style={{ color: '#F59E0B' }} />
                    )}
                    <a
                      href={service.providerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded hover:bg-white/10 transition-colors"
                      style={{ color: '#7CD4FD' }}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-6 pt-4 border-t flex flex-wrap gap-4" style={{ borderColor: '#1F2937' }}>
            <div className="flex items-center gap-2 text-xs" style={{ color: '#94A3B8' }}>
              <CheckCircle className="w-4 h-4" style={{ color: '#22C55E' }} />
              Konfiguriert
            </div>
            <div className="flex items-center gap-2 text-xs" style={{ color: '#94A3B8' }}>
              <AlertCircle className="w-4 h-4" style={{ color: '#F59E0B' }} />
              Nicht konfiguriert
            </div>
            <div className="flex items-center gap-2 text-xs" style={{ color: '#94A3B8' }}>
              <ExternalLink className="w-4 h-4" style={{ color: '#7CD4FD' }} />
              Zum Anbieter
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#1E293B', borderLeft: '4px solid #7CD4FD' }}>
        <h3 className="font-semibold mb-2" style={{ color: '#7CD4FD' }}>💡 Hinweise</h3>
        <ul className="text-sm space-y-1" style={{ color: '#94A3B8' }}>
          <li>• <strong>DEV-Modus:</strong> Spart Credits - nutzt nur OpenAI direkt</li>
          <li>• <strong>PROD-Modus:</strong> Volle KI-Power mit Gemini + GPT-4o + Claude</li>
          <li>• <strong>Neustart:</strong> Erforderlich nach Modus-Änderung für permanente Wirkung</li>
          <li>• <strong>API-Keys:</strong> Konfigurieren Sie alle Keys unter Einstellungen</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminServiceDashboard;
